import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useBodyScrollLock } from "../../../hooks/useBodyScrollLock";
import { X } from "lucide-react";
import { FiMaximize2 } from "react-icons/fi";

export const GalleryGrid = ({ galleryItems }) => {
  const [selectedImg, setSelectedImg] = useState(null);
  useBodyScrollLock(selectedImg);

  const handleImageClick = (item) => {
    setSelectedImg(item.src);
  };

  const closeModal = () => {
    setSelectedImg(null);
  };

  // Handle Escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") closeModal();
    };
    if (selectedImg) {
      document.addEventListener("keydown", handleEscapeKey);
      return () => document.removeEventListener("keydown", handleEscapeKey);
    }
  }, [selectedImg]);

  return (
    <div>
      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3   gap-6 py-6 md:py-10">
        {galleryItems.map((item, index) => (
          <motion.div
            key={index}
            className="flex flex-col space-y-3"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.08 }}
            viewport={{ once: true }}
          >
            {/* Image Card */}
            <div
              className="overflow-hidden rounded-lg shadow-md cursor-pointer group bg-white hover:shadow-xl transition-all duration-300"
              onClick={() => handleImageClick(item)}
            >
              <div className="relative overflow-hidden">
                <img
                  src={item.src}
                  alt={item.title || "gallery image"}
                  className="w-full h-56 md:h-64 object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300" />
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform scale-75 group-hover:scale-100">
                    <div className="bg-white/90 backdrop-blur-sm text-gray-700 p-3 rounded-full shadow-lg">
                      <FiMaximize2 className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Optional Title */}
            {item.title && (
              <motion.h3
                className="text-center text-[#333] font-semibold text-sm sm:text-base px-2 leading-tight"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: index * 0.08 + 0.2 }}
                viewport={{ once: true }}
              >
                {item.title}
              </motion.h3>
            )}
          </motion.div>
        ))}
      </div>

      {/* Fullscreen Lightbox */}
      <AnimatePresence>
        {selectedImg && (
          <motion.div
            className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            role="dialog"
            aria-modal="true"
          >
            <motion.div
              className="relative w-full max-w-6xl flex justify-center"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={closeModal}
                className="absolute -top-12 right-0 text-white bg-black/50 hover:bg-black/70 p-3 rounded-full transition-colors duration-200 z-10"
                aria-label="Close image preview"
              >
                <X size={24} />
              </button>

              {/* ESC hint */}
              <div className="absolute -top-10 left-0 text-white/70 text-sm bg-black/50 px-3 py-2 rounded-lg">
                Press ESC to close
              </div>

              {/* Image */}
              <img
                src={selectedImg}
                alt="Selected preview"
                className="w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                style={{
                  maxHeight: "calc(100vh - 120px)",
                  maxWidth: "calc(100vw - 32px)",
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MiddleContentService = ({ service }) => {
  // Normalize images into { src, title }
  const images =
    service.images
      ?.slice(6)
      .map((img) =>
        typeof img === "string" ? { src: img, title: "" } : img
      ) || [];

  return (
    <div className="mt-2">
      {service.description && service.description.length > 0 ? (
        <div className="space-y-8">
          {service.description.map((desc, index) => (
            <div key={index} className="group">
              <h2 className="text-lg sm:text-xl my-2 md:text-2xl font-bold text-gray-700 font-onest leading-tight ">
                {desc.heading}
              </h2>
              <div className="prose prose-lg text-sm sm:text-base text-gray-700 tracking-wide leading-relaxed">
                <p className=" break-words whitespace-pre-line">
                  {desc.content}
                </p>
              </div>
              {index < service.description.length - 1 && (
                <div className="border-b border-gray-300 my-2 md:my-8"></div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">📝</div>
          <h3 className="text-gray-600 font-semibold text-xl mb-2">
            No Description Available
          </h3>
          <p className="text-gray-500">
            This service doesn't have a detailed description yet.
          </p>
        </div>
      )}
      {images.length >= 1 && (
        <div>
          <h2 className="text-lg sm:text-xl mt-4 sm:mt-6 md:text-2xl font-bold text-gray-800 font-onest leading-tight ">
            Gallery Section
          </h2>
          <GalleryGrid galleryItems={images} />
        </div>
      )}
    </div>
  );
};

export default MiddleContentService;
